import { Handle, Position } from '@xyflow/react'
import { GitBranch, X } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { GraphNodeData } from '@/types/graph'
import { useCanvasStore } from '@store/canvas'

interface ConditionNodeProps {
  data: GraphNodeData
  selected?: boolean
  id: string
}

export default function ConditionNode({
  data,
  selected,
  id,
}: ConditionNodeProps) {
  const { t } = useTranslation()
  const removeNode = useCanvasStore((state) => state.removeNode)

  const expressionPreview = data.config?.logic
    ? String(data.config.logic).substring(0, 60)
    : t('build.noConditionSet')

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation()
    removeNode(id)
  }

  return (
    <div
      className={`px-4 py-3 rounded-lg border-2 bg-white shadow-md min-w-48 transition-all ${
        selected
          ? 'border-agent-orange ring-2 ring-agent-orange/30 shadow-lg'
          : 'border-agent-orange/50 hover:border-agent-orange'
      }`}
    >
      <Handle type="target" position={Position.Top} />

      {/* Header */}
      <div className="flex items-center justify-between mb-2 group">
        <div className="flex items-center gap-2 flex-1">
          <GitBranch size={16} className="text-agent-orange flex-shrink-0" />
          <div className="font-bold text-sm text-gray-900 truncate">
            {data.label || t('build.conditionalExpression')}
          </div>
        </div>
        <button
          onClick={handleDelete}
          className="opacity-0 group-hover:opacity-100 transition-opacity p-1 hover:bg-red-100 rounded text-red-600"
          title={t('build.deleteNode')}
        >
          <X size={14} />
        </button>
      </div>

      {/* Body - rotated diamond visual hint */}
      <div className="text-xs text-gray-600 mb-3 bg-yellow-50 rounded p-2 border-l-2 border-agent-orange">
        <div className="font-mono text-gray-700 text-xs truncate">
          {expressionPreview}
        </div>
      </div>

      {/* Footer - Branch labels */}
      <div className="flex gap-2 text-xs font-semibold">
        <span className="px-2 py-1 bg-green-100 text-green-700 rounded flex-1 text-center">
          {t('build.trueLabel')}
        </span>
        <span className="px-2 py-1 bg-red-100 text-red-700 rounded flex-1 text-center">
          {t('build.falseLabel')}
        </span>
      </div>

      {/* True branch handle */}
      <Handle
        type="source"
        position={Position.Bottom}
        id="true"
        style={{ left: '25%' }}
      />

      {/* False branch handle */}
      <Handle
        type="source"
        position={Position.Bottom}
        id="false"
        style={{ left: '75%' }}
      />
    </div>
  )
}
